import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNews } from "@/context/NewsContext";
import { useT } from "@/i18n/useLocale";
import T from "@/i18n/T";

const DATE_LABELS: Record<string, string> = {
  today: "Today",
  "3days": "Last 3 days",
  week: "This week",
  "2weeks": "Last 2 weeks",
  month: "This month",
  "3months": "Last 3 months",
};

const ActiveFilterPills = () => {
  const t = useT();
  const { filters, setFilter, availableSources } = useNews();

  const sourceNameById = new Map(availableSources.map(s => [s.id, s.name]));
  const hasAny =
    filters.sourceIds.length > 0 ||
    filters.dateRange !== "all" ||
    filters.sort !== "latest";

  if (!hasAny) return null;

  const removeSource = (id: string) =>
    setFilter("sourceIds", filters.sourceIds.filter(s => s !== id));

  return (
    <div className="flex flex-wrap items-center gap-2 py-3">
      {filters.sort !== "latest" && (
        <Badge variant="secondary" className="gap-1 pl-3 pr-1 h-7 rounded-full">
          Sort: {filters.sort === "trending" ? "Trending" : "Most discussed"}
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 rounded-full hover:bg-background/60"
            onClick={() => setFilter("sort", "latest")}
            aria-label={t("auto.reset-sort-4673")}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )}
      {filters.dateRange !== "all" && (
        <Badge variant="secondary" className="gap-1 pl-3 pr-1 h-7 rounded-full">
          {DATE_LABELS[filters.dateRange] ?? filters.dateRange}
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 rounded-full hover:bg-background/60"
            onClick={() => setFilter("dateRange", "all")}
            aria-label={t("auto.clear-date-range-2fb4")}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )}
      {filters.sourceIds.map(id => (
        <Badge key={id} variant="secondary" className="gap-1 pl-3 pr-1 h-7 rounded-full">
          {sourceNameById.get(id) ?? "Source"}
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 rounded-full hover:bg-background/60"
            onClick={() => removeSource(id)}
            aria-label={t("auto.remove-source-d074")}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-muted-foreground"
        onClick={() => {
          setFilter("sourceIds", []);
          setFilter("dateRange", "all");
          setFilter("sort", "latest");
        }}
      >
        <T k="auto.clear-all-81aa" />
      </Button>
    </div>
  );
};

export default ActiveFilterPills;
